/**
 * Semantic event derivation for the Observ RUM SDK (FR-3 / D8, story 2.3).
 *
 * Derives high-value interaction facts from native DOM listeners and emits them
 * as OTel log records (`observ.session.*`) on the logs channel (`/v1/logs`):
 *   - `observ.session.click`      — every click, with `element.selector` / `element.text`
 *   - `observ.session.rage_click` — ≥ 3 clicks < 1 s on the SAME selector
 *   - `observ.session.navigate`   — SPA navigation (`pushState`/`replaceState`/`popstate`), `url`
 *
 * Variance vs architecture D8 ("tap rrweb emit"): we use native DOM listeners
 * rather than reverse-mapping rrweb node ids — `element.selector`/`element.text`
 * are derived directly and reliably from `event.target`. Same OTel-logs output.
 *
 * Invariant (1.1/1.2/1.3): never break the host page. Listeners run in the
 * page's own context, so every handler is `try/catch`-guarded. SPA navigation is
 * detected through the shared {@link import('./history-nav.js')} observer (which
 * patches `history` once for both this tap and page-view tracking), unsubscribed
 * on `stop()` so no global stays monkey-patched.
 */
import type { Logger } from '@opentelemetry/api-logs'

import { onHistoryNavigation } from './history-nav.js'
import { emitSessionEvent, setupOtelLogs } from './otel-logs.js'
import { cssSelector, elementText } from './selector.js'
import type { ObservInitOptions } from './types.js'

/** Rage-click window (ms) and click threshold (architecture D8 / AC#2). */
export const RAGE_CLICK_WINDOW_MS = 1_000
export const RAGE_CLICK_THRESHOLD = 3
/** Cap on tracked selectors so the rage-click map can't grow unbounded over a
 *  long session (cleared past this — rage detection is best-effort). */
const MAX_TRACKED_SELECTORS = 100

/** Module-level guard so the tap is self-idempotent (a second start without a
 *  stop() between would double-patch `history` / double-add listeners). */
let active: SemanticEventsHandle | null = null

/** Public handle for the running semantic-event tap. */
export interface SemanticEventsHandle {
  /** Remove listeners, restore patched `history`, shut the logs provider down. */
  stop(): Promise<void>
}

const NOOP: SemanticEventsHandle = { stop: () => Promise.resolve() }

/** Resolve an `EventTarget` to the nearest `Element` (text/SVG nodes → parent). */
function toElement(target: EventTarget | null): Element | null {
  if (target instanceof Element) return target
  const node = target as Node | null
  return node && node.parentElement ? node.parentElement : null
}

/**
 * Start deriving + emitting semantic events. Returns a handle whose `stop()`
 * tears everything down. Never throws; degrades to a no-op in a non-DOM env.
 * NOTE: not gated by `disableReplay` — semantic events are useful even without
 * the heavy replay capture.
 */
export function startSemanticEvents(options: ObservInitOptions): SemanticEventsHandle {
  if (active) return active
  if (typeof document === 'undefined' || typeof window === 'undefined') return NOOP

  let logger: Logger
  try {
    logger = setupOtelLogs(options)
  } catch (err) {
    console.warn('[observ] semantic events: logs setup failed', err)
    return NOOP
  }

  // Recent click timestamps per selector, for rage-click clustering.
  const clickTimes = new Map<string, number[]>()

  const onClick = (event: Event): void => {
    try {
      const el = toElement(event.target)
      if (!el) return
      const selector = cssSelector(el)
      emitSessionEvent(logger, 'observ.session.click', {
        'element.selector': selector,
        'element.text': elementText(el),
      })

      // Rage-click: ≥ THRESHOLD clicks within WINDOW on the same selector.
      const now = Date.now()
      const recent = (clickTimes.get(selector) ?? []).filter((t) => now - t < RAGE_CLICK_WINDOW_MS)
      recent.push(now)
      if (recent.length >= RAGE_CLICK_THRESHOLD) {
        emitSessionEvent(logger, 'observ.session.rage_click', { 'element.selector': selector })
        clickTimes.delete(selector) // reset the burst (one rage_click per burst)
      } else {
        // Bound the map: a selector clicked once never reaches the threshold and
        // would otherwise linger forever. Clear past the cap (rare; best-effort).
        if (clickTimes.size > MAX_TRACKED_SELECTORS) clickTimes.clear()
        clickTimes.set(selector, recent)
      }
    } catch {
      /* never let a click handler break the host page */
    }
  }

  document.addEventListener('click', onClick, { capture: true })

  // SPA navigation (pushState/replaceState/popstate) via the shared observer.
  const unsubscribeNav = onHistoryNavigation(() => {
    emitSessionEvent(logger, 'observ.session.navigate', { url: location.href })
  })

  let stopped = false
  const handle: SemanticEventsHandle = {
    async stop(): Promise<void> {
      if (stopped) return
      stopped = true
      active = null
      document.removeEventListener('click', onClick, { capture: true })
      unsubscribeNav()
      // The shared logs provider is shut down once by index.shutdown() (story
      // 2.4) — not here, since js-errors shares it.
    },
  }
  active = handle
  return handle
}
